import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { OutreachDraft, OutreachTemplateLintWarning } from '@discovr/types';
import { Button } from '../../../../components/ui/button';
import { Sparkle } from '../../../../design-system/icons';
import { AiFailureNotice } from '../AiFailureNotice';
import { aiFailureOfReason, isAiOutcomeReason, isSubjectReason, toastAiFailure } from '../aiFailure';
import { DraftCard, type DraftCardEdit } from '../DraftCard';
import { draftPlaceholder, isIncompleteDraft, isOverBodyCap, isOverSubjectCap, placeholderCopy, type DraftSaveError } from '../draftLabels';
import { sendErrorMessage } from '../sendLabels';
import { lintLabel } from '../templateLintLabels';
import { draftOutcomeLabel, draftingLine, notInThisSendLine, templateWarningsLead, willWaitLine } from './bulkSendCopy';
import type { BatchDraftItem } from './useBatchDrafts';
import { effectiveRecipient, sendableTargets, type WizardState } from './wizardState';
import { useLiveText } from './useLiveText';
import { WizardStepHeader } from './WizardStepHeader';

type ReadyItem = Extract<BatchDraftItem, { state: 'ready' }>;
/** Not ready: the creators Continue would leave out. */
type DroppedItem = Extract<BatchDraftItem, { state: 'writing' | 'failed' | 'skipped' }>;

export const BATCH_EXPIRED_NOTE = 'This batch has expired. Draft again to continue — edits you made to these drafts are replaced.'; // copy: ours
export const EARLIER_DRAFT_NOTE = 'An earlier draft is still waiting; Retry writes a new one.'; // copy: ours
export const NO_EMAIL_DRAFT_COPY = 'No email yet — this draft waits for an email and goes out once one is added.'; // copy: ours

export interface DraftsStepProps {
  state: WizardState;
  /** The operation useBatchDrafts is currently polling — lets a pending Retry tell "still waiting
   *  on MY retry" apart from "a later retry took over the single tracked operation". */
  operationId: string | null;
  onOperationIdChange: (operationId: string | null) => void;
  /** Drafts one creator again from the batch's template; resolves with the new operation. */
  onRetryCreator: (entryId: string) => Promise<{ operationId: string }>;
  /** The "Draft N of M" menu at a ready card's top right (R17-66); "Write a new draft" in its footer uses Retry's call. */
  renderDraftMenu?: (draft: OutreachDraft) => ReactNode;
  /** The Template button on a first touch the AI could not write: parks step 1 of the batch's template as a draft for review. */
  onTemplateCreator?: (entryId: string) => Promise<void>;
  /** The drafting request is on its way, before any outcome exists. */
  starting: boolean;
  /** Deliverability lint from saving this batch's template: advisory, never blocks Continue or Send. */
  templateWarnings?: OutreachTemplateLintWarning[];
  /** Why the drafting request failed; absent when it did not. */
  startError: string | null;
  onRetryStart: () => void;
  /** The wizard batch is older than Go accepts, so these drafts can't be sent as they are. */
  batchExpired?: boolean;
  /** Drafts every creator in this send again, which makes a new batch. */
  onRedraft?: () => void;
  /** Without write access the drafts are only viewed: no Retry, Template, Draft again or edit. Defaults to true. */
  canWrite?: boolean;
  items: BatchDraftItem[];
  loadError: boolean;
  onRetryLoad: () => void;
  /** Message ids whose draft was saved from this wizard session. */
  editedIds: number[];
  /** Message ids whose save is in flight. */
  savingIds: number[];
  cardErrors: Record<number, DraftSaveError | undefined>;
  onSaveDraft: (draft: OutreachDraft, edit: DraftCardEdit) => void;
  onDismissError: (messageId: number) => void;
  /** Lets BulkSendWizard block Continue while any ready draft here is mid-edit. */
  onEditingBlockChange?: (blocked: boolean) => void;
}

/** Saving writes the draft only; the send is approved on the last step. */
const EDIT_NOTE = 'Saving updates this draft. Nothing is sent until you approve it on the last step.';

function notReadyStatus(item: Exclude<BatchDraftItem, ReadyItem>, startFailed: boolean): { text: string; cls: string } {
  if (item.state === 'writing' && startFailed) return { text: 'Not started', cls: 'text-rose-700' };
  if (item.state === 'writing') return { text: 'Generating…', cls: 'animate-pulse text-muted-foreground motion-reduce:animate-none' };
  if (item.state === 'failed') return { text: `Failed · ${draftOutcomeLabel(item.reason)}`, cls: 'text-rose-700' };
  return { text: `Skipped · ${draftOutcomeLabel(item.reason)}`, cls: 'text-muted-foreground' };
}

function droppedLabel(item: DroppedItem): string {
  return item.state === 'writing' ? `@${item.username} (still generating)` : `@${item.username} (${draftOutcomeLabel(item.reason)})`;
}

/** Step 3: the batch's drafts as they are written, each reviewable and editable in place. */
export function DraftsStep({
  state, operationId, onOperationIdChange, onRetryCreator, renderDraftMenu, onTemplateCreator, starting, templateWarnings = [], startError, onRetryStart, batchExpired = false, onRedraft, canWrite = true, items: batchItems, loadError, onRetryLoad,
  editedIds, savingIds, cardErrors, onSaveDraft, onDismissError, onEditingBlockChange,
}: DraftsStepProps) {
  const [retryError, setRetryError] = useState<string | null>(null);
  // Per-creator Retry: entryId -> its retry operation id (null while the request is in flight).
  // The entry shows as writing until its item stops being a failure that predates the retry,
  // so the stale "Failed" can't offer Retry again and mint duplicate operations.
  const [retrying, setRetrying] = useState<Record<string, string | null>>({});
  // A real new outcome landing under MY OWN retry clears it — render-time, reacts to batchItems.
  const settledByOutcome = Object.keys(retrying).filter((entryId) => {
    const item = batchItems.find((candidate) => candidate.entryId === entryId);
    const myRetryOp = retrying[entryId];
    if (!item || item.state !== 'failed') return true;
    if (myRetryOp === null) return false; // still waiting on the request itself
    return item.operationId === myRetryOp;
  });
  if (settledByOutcome.length > 0) {
    setRetrying((prev) => Object.fromEntries(Object.entries(prev).filter(([entryId]) => !settledByOutcome.includes(entryId))));
  }
  // If a later retry (any entry) wins useBatchDrafts' single tracked-operation slot before
  // mine ever reported, give up rather than hang on "Generating…" forever. Reacts to operationId
  // PROP transitions only, not every render — not an effect (cascading-render lint).
  const [lastOperationId, setLastOperationId] = useState(operationId);
  if (lastOperationId !== operationId) {
    setLastOperationId(operationId);
    setRetrying((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([, myRetryOp]) => myRetryOp === null || myRetryOp === operationId));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }
  const items: BatchDraftItem[] = batchItems.map((item) =>
    item.entryId in retrying && !settledByOutcome.includes(item.entryId) ? { entryId: item.entryId, username: item.username, state: 'writing' } : item,
  );
  // Step-local: only gates this step's own Continue (via the callback above).
  const [editingIds, setEditingIds] = useState<number[]>([]);
  const handleEditingChange = (id: number, editing: boolean) =>
    setEditingIds((prev) => (editing ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id)));
  const count = sendableTargets(state).length;
  const ready = items.filter((item): item is ReadyItem => item.state === 'ready');
  const waitingCount = ready.filter((item) => !effectiveRecipient(item.draft)).length;
  const headerSub = `${count} ${count === 1 ? 'creator' : 'creators'} selected.`;
  // The drafts never started: nothing is being written, so the rows stop pulsing and the progress goes quiet.
  const startFailed = !!startError && !starting;
  const writing = startFailed ? 0 : items.filter((item) => item.state === 'writing').length;
  const settled = items.length - writing;
  const progress = useLiveText(startFailed ? '' : starting && items.length === 0 ? 'Starting the drafts…' : writing > 0 ? draftingLine(settled, items.length) : items.length > 0 ? 'Drafting finished.' : '');
  const readyIds = ready.map((item) => item.draft.message.id);
  const editingReady = editingIds.filter((id) => readyIds.includes(id));
  const incompleteReady = ready.filter(({ draft }) => isIncompleteDraft(draft));
  const overSubjectReady = ready.filter(({ draft }) => isOverSubjectCap(draft));
  const overCapReady = ready.filter(({ draft }) => isOverBodyCap(draft));
  const placeholderReady = ready.filter(({ draft }) => !!draftPlaceholder(draft));

  // The admin toast names each raw reason once, with how many creators it hit; a Retry that fails the same way is new news.
  const toasted = useRef(new Set<string>());
  useEffect(() => {
    const fresh = new Map<string, number>();
    for (const item of batchItems) {
      if (item.state !== 'failed' || !isAiOutcomeReason(item.reason)) continue;
      const key = `${item.entryId}|${item.operationId ?? ''}|${item.reason}`;
      if (toasted.current.has(key)) continue;
      toasted.current.add(key);
      fresh.set(item.reason, (fresh.get(item.reason) ?? 0) + 1);
    }
    for (const [reason, count] of fresh) {
      const failure = aiFailureOfReason(reason);
      if (failure) toastAiFailure(failure, count > 1 ? `${count} creators` : undefined);
    }
  }, [batchItems]);

  // Reports up so BulkSendWizard's Continue (rendered outside this step) blocks while a ready card here is
  // mid-edit — otherwise Continue unmounts this step and silently discards the unsaved edit (Finding 3).
  useEffect(() => {
    onEditingBlockChange?.(editingReady.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingReady.length]);

  // The new operation replaces the tracked one, or useBatchDrafts keeps polling the stale, already-done
  // operation and the retried entry is left depending on the SSE event alone.
  const retry = (entryId: string) => {
    setRetrying((prev) => ({ ...prev, [entryId]: null }));
    setRetryError(null);
    onRetryCreator(entryId)
      .then(({ operationId: id }) => {
        setRetrying((prev) => (entryId in prev ? { ...prev, [entryId]: id } : prev));
        onOperationIdChange(id);
      })
      .catch((reason: unknown) => {
        setRetrying((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== entryId)));
        setRetryError(sendErrorMessage(reason));
      });
  };

  // Shows the entry as writing until its parked draft is read back; a failure leaves the item failed.
  const templateFor = (entryId: string) => {
    if (!onTemplateCreator) return;
    setRetrying((prev) => ({ ...prev, [entryId]: null }));
    setRetryError(null);
    onTemplateCreator(entryId)
      .then(() => setRetrying((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== entryId))))
      .catch((reason: unknown) => {
        setRetrying((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== entryId)));
        setRetryError(sendErrorMessage(reason));
      });
  };

  const dropped = items.filter((item): item is DroppedItem => item.state === 'writing' || item.state === 'failed' || item.state === 'skipped');
  const gate = ready.length === 0
    ? 'Continue unlocks when at least one draft is ready.'
    : dropped.length > 0 ? `${notInThisSendLine(dropped.length)}: ${dropped.map(droppedLabel).join(', ')}.` : '';

  return (
    <div className="mx-auto w-full max-w-[880px] space-y-5">
      <WizardStepHeader heading="Drafts" sub={headerSub} />
      {waitingCount > 0 ? <p className="-mt-3 text-body-sm-medium text-amber-700">{willWaitLine(waitingCount)}</p> : null}
      <p role="note" className="flex items-start gap-2 rounded-xl bg-(--brand-50,#f5f3ff) p-3 text-body-sm-regular text-foreground">
        <Sparkle size={16} className="mt-0.5 shrink-0 text-(--brand-500)" />
        <span><span className="font-medium">The assistant writes each email.</span> It starts from your template and the campaign brief and personalises it per creator. You review every draft before anything goes out — nothing sends until you approve.</span>
      </p>
      {templateWarnings.length > 0 ? (
        <section aria-label="Template warnings" className="space-y-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-body-sm-medium text-amber-900">{templateWarningsLead(templateWarnings.length)}</p>
          <ul className="list-disc space-y-0.5 pl-5 text-caption-regular text-amber-800">
            {templateWarnings.map((w, i) => <li key={`${w.stepNo}-${w.code}-${i}`}>{`${w.stepNo > 0 ? `Step ${w.stepNo}` : 'Signature'}: ${lintLabel(w)}`}</li>)}
          </ul>
        </section>
      ) : null}
      {writing > 0 ? (
        <div
          role="progressbar"
          aria-label="Drafting progress"
          aria-valuemin={0}
          aria-valuemax={items.length}
          aria-valuenow={settled}
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full rounded-full bg-foreground transition-[width] motion-reduce:transition-none" style={{ width: `${items.length === 0 ? 0 : Math.round((settled / items.length) * 100)}%` }} />
        </div>
      ) : null}
      <p role="status" className="text-body-sm-regular text-muted-foreground empty:hidden">{progress}</p>
      {loadError ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-body-sm-regular text-rose-800">
          <span>Couldn&apos;t load the drafts. Try again.</span>
          <Button size="sm" variant="outline" onClick={onRetryLoad}>Retry</Button>
        </div>
      ) : null}
      {batchExpired ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-body-sm-regular text-amber-900">
          <span>{BATCH_EXPIRED_NOTE}</span>
          {onRedraft && canWrite ? <Button size="sm" variant="outline" disabled={starting} onClick={onRedraft}>Draft again</Button> : null}
        </div>
      ) : null}
      {startError || retryError ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-body-sm-regular text-rose-800">
          <span>{startError ?? retryError}</span>
          {startError && canWrite ? <Button size="sm" variant="outline" disabled={starting} onClick={onRetryStart}>Retry</Button> : null}
        </div>
      ) : null}
      <ul className="space-y-3">
        {items.map((item) => {
          if (item.state !== 'ready') {
            const status = notReadyStatus(item, startFailed);
            // The AI could not write it: one error + Retry, and Template for a first touch (a subject failure waits behind Retry).
            // Go refuses a Template while an older draft waits, so that creator gets Retry only.
            const aiFailure = item.state === 'failed' ? aiFailureOfReason(item.reason, item.retryable) : null;
            const templateWanted = canWrite && !!aiFailure && !!onTemplateCreator && !isSubjectReason(item.state === 'failed' ? item.reason : '');
            const earlierDraft = item.state === 'failed' && !!item.earlierDraft;
            const templateOffered = templateWanted && !earlierDraft;
            return (
              <li key={item.entryId} aria-label={`Draft for ${item.username}`} className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border p-3 text-body-sm-regular">
                <span className="text-foreground">@{item.username}</span>
                {item.state === 'failed' && aiFailure ? (
                  <div className="flex flex-col items-end gap-1">
                    <AiFailureNotice
                      role="status"
                      className="justify-end"
                      failure={aiFailure}
                      label={`draft for ${item.username}`}
                      onRetry={canWrite ? () => retry(item.entryId) : undefined}
                      onTemplate={templateOffered ? () => templateFor(item.entryId) : undefined}
                    />
                    {templateWanted && earlierDraft ? <p className="text-caption-regular text-muted-foreground">{EARLIER_DRAFT_NOTE}</p> : null}
                  </div>
                ) : (
                  <span className="flex items-center gap-3">
                    <span className={status.cls}>{status.text}</span>
                    {item.state === 'failed' && canWrite ? (
                      <Button size="sm" variant="outline" aria-label={`Retry draft for ${item.username}`} onClick={() => retry(item.entryId)}>
                        Retry
                      </Button>
                    ) : null}
                  </span>
                )}
              </li>
            );
          }
          const id = item.draft.message.id;
          const saveError = cardErrors[id];
          const placeholder = draftPlaceholder(item.draft);
          return (
            <li key={item.entryId} className="list-none space-y-1">
              {editedIds.includes(id) ? <p className="text-caption-regular text-emerald-700">Edited</p> : null}
              {placeholder ? <p role="alert" className="text-caption-regular text-destructive">{placeholderCopy(placeholder)}</p> : null}
              {effectiveRecipient(item.draft) ? null : <p className="text-caption-regular text-amber-700">{NO_EMAIL_DRAFT_COPY}</p>}
              <DraftCard
                draft={item.draft}
                canWrite={canWrite}
                approveMode="editOnly"
                editNote={EDIT_NOTE}
                busy={savingIds.includes(id)}
                error={saveError && !saveError.field ? saveError.message : null}
                fieldError={saveError?.field ? saveError : null}
                onDismissError={() => onDismissError(id)}
                onApprove={(edit) => {
                  if (edit) onSaveDraft(item.draft, edit);
                }}
                onEditingChange={(editing) => handleEditingChange(id, editing)}
                variant="wizard"
                draftMenu={renderDraftMenu?.(item.draft)}
                onWriteNew={canWrite && renderDraftMenu ? () => retry(item.entryId) : undefined}
              />
            </li>
          );
        })}
      </ul>
      {editingReady.length > 0 ? (
        <p role="status" className="text-caption-regular text-muted-foreground">Save or cancel your edit first.</p>
      ) : incompleteReady.length > 0 ? (
        <p role="status" className="text-caption-regular text-destructive">
          {`Every first email needs a subject and a message: ${incompleteReady.map((item) => `@${item.username}`).join(', ')}.`}
        </p>
      ) : overSubjectReady.length > 0 ? (
        <p role="status" className="text-caption-regular text-destructive">
          {`Subject too long for the 60-character limit, shorten before continuing: ${overSubjectReady.map((item) => `@${item.username}`).join(', ')}.`}
        </p>
      ) : overCapReady.length > 0 ? (
        <p role="status" className="text-caption-regular text-destructive">
          {`Too long for the 2,000-character limit, shorten before continuing: ${overCapReady.map((item) => `@${item.username}`).join(', ')}.`}
        </p>
      ) : placeholderReady.length > 0 ? (
        <p role="status" className="text-caption-regular text-destructive">
          {`Fill in or remove the placeholder before continuing: ${placeholderReady.map((item) => `@${item.username}`).join(', ')}.`}
        </p>
      ) : gate ? (
        <p role="status" className="text-caption-regular text-muted-foreground">{gate}</p>
      ) : null}
    </div>
  );
}
